import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authClient, useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import SectionHeader from "@/components/SectionHeader";
import { sfx } from "@/lib/sounds";
import { UserPlus, Check, X, Clock } from "lucide-react";

function timeAgo(ts) {
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ""; }
}

const TABS = [
  { key: "incoming", label: "Incoming" },
  { key: "outgoing", label: "Outgoing" },
];

export default function FollowRequests() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("incoming");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) nav("/"); }, [loading, user, nav]);

  const load = async () => {
    setBusy(true);
    try {
      const path = tab === "incoming" ? "/follow/requests" : "/follow/requests/outgoing";
      const { data } = await authClient.get(path);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [tab, user]);

  const act = async (id, verb) => {
    try {
      if (verb === "accept") { await authClient.post(`/follow/requests/${id}/accept`); sfx.pling(); toast.success("Follow request accepted"); }
      else if (verb === "reject") { await authClient.post(`/follow/requests/${id}/reject`); sfx.no(); toast.success("Rejected"); }
      else if (verb === "cancel") { await authClient.delete(`/follow/requests/${id}`); sfx.no(); toast.success("Cancelled"); }
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };

  if (!user) return null;

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-follow-requests">
      <div className="max-w-3xl mx-auto">
        <SectionHeader eyebrow="Follow Requests" title="Manage requests" subtitle="Approve people who want to follow your private profile." />
        <div className="flex gap-1 mb-6 border-b border-white/10">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); sfx.click(); }}
                    data-testid={`fr-tab-${t.key}`}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${
                      tab === t.key ? "border-[#FFBF00] text-[#FFBF00]" : "border-transparent text-white/60 hover:text-white"
                    }`}>
              {t.label}
            </button>
          ))}
        </div>
        {busy && <div className="text-center text-white/40 py-6">Loading…</div>}
        {!busy && rows.length === 0 && (
          <div className="rounded-2xl glass p-12 text-center text-white/50">
            <UserPlus className="mx-auto mb-3 opacity-40" />
            {tab === "incoming" ? "No pending follow requests." : "You have no outgoing requests."}
          </div>
        )}
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl glass p-4 flex items-center gap-3" data-testid={`fr-row-${r.id}`}>
              <Link to={`/users/${tab === "incoming" ? r.requester_id : r.target_id}`}>
                <img src={r.requester_avatar || `https://api.dicebear.com/9.x/pixel-art-neutral/svg?seed=${r.id}`}
                     alt="" className="w-11 h-11 rounded-lg" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/users/${tab === "incoming" ? r.requester_id : r.target_id}`} className="font-semibold hover:text-[#FFBF00]">
                  {r.requester_name || "User"}
                </Link>
                <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                  <Clock size={10} /> {timeAgo(r.created_at)}
                </div>
              </div>
              {tab === "incoming" ? (
                <div className="flex gap-2">
                  <button onClick={() => act(r.id, "accept")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-[10px] uppercase tracking-widest font-semibold">
                    <Check size={11} /> Accept
                  </button>
                  <button onClick={() => act(r.id, "reject")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-red-400/40 text-white/70 text-[10px] uppercase tracking-widest">
                    <X size={11} /> Reject
                  </button>
                </div>
              ) : (
                <button onClick={() => act(r.id, "cancel")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-red-400/40 text-white/70 text-[10px] uppercase tracking-widest">
                  <X size={11} /> Cancel
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
